import { IEvent, IAlert, IWSAlert, NotificationSeverity } from '../../models/types';
import { notificationTypesData } from '../../constants';

/** API-контракт показа «Отложить»: postponeTo + непрочитано + срок в будущем. */
const isPostponeAvailable = (
  message: { postponeTo?: string | null; readTime?: string | null },
  now: number = Date.now()
): boolean => {
  if (message.postponeTo == null || message.readTime != null) {
    return false;
  }
  const postponeToTime = new Date(message.postponeTo).getTime();
  return Number.isFinite(postponeToTime) && postponeToTime > now;
};

/** Merge без затирания полей, которых нет в payload (undefined). Явный null сохраняется. */
const mergeAlertUpdate = (existing: IAlert, payload: IAlert): IAlert => {
  const updated: IAlert = { ...existing };
  (Object.keys(payload) as Array<keyof IAlert>).forEach(key => {
    const value = payload[key];
    if (value !== undefined) {
      (updated as any)[key] = value;
    }
  });
  return updated;
};

const convertNotificationToAlert = (notification: IEvent): IAlert => {
  const defaultNotificationClassIcon = { color: '' };
  const { color } = notificationTypesData[notification.class] || defaultNotificationClassIcon;

  return {
    uid: notification.eventUid,
    userId: notification.userId,
    type: notification.class,
    title: notification.classFriendly,
    description: notification.platformText,
    color,
    isConfirm: !notification.confirmRead,
    date: notification.eventTime,
    severity: notification.severity,
    // Навигационные поля для перехода на карту
    areaId: notification.areaId || null,
    mapId: notification.mapId || null,
    lat: typeof notification.lat === 'number' ? notification.lat : null,
    lon: typeof notification.lon === 'number' ? notification.lon : null,
    alt: typeof notification.alt === 'number' ? notification.alt : null
    // trackInfo: notification.trackInfo
  };
};

const convertWSAlertToAlert = (alert: IWSAlert): IAlert => {
  const defaultNotificationClassIcon = { color: '' };
  const { color } = notificationTypesData[alert.class] || defaultNotificationClassIcon;

  const lat = typeof alert.lat === 'number' ? alert.lat : null;
  const lon = typeof alert.lon === 'number' ? alert.lon : null;
  const alt = typeof alert.alt === 'number' ? alert.alt : null;

  const flags = String(alert?.templateData?.confirmationFlags || '').toUpperCase();
  const needConfirmByFlags = flags !== 'NONE' && flags !== '';
  const needConfirm =
    typeof alert.isNeedConfirmation === 'boolean' ? alert.isNeedConfirmation : needConfirmByFlags;
  const isUnread = !alert.readTime;

  const result: IAlert = {
    uid: alert.messageId,
    userId: alert.userId,
    type: alert.class,
    title: alert.classFriendly,
    description: alert.text || alert.neutralText || '',
    color,
    isConfirm: needConfirm && isUnread,
    date: alert.messageTime || alert.eventTime,
    severity: alert.messageSeverity as NotificationSeverity,
    officeId: alert.officeId ?? null,
    placeId: alert.placeId ?? alert.officeId ?? null,
    mapId: alert.mapId ?? null,
    areaId: alert.areaId ?? null,
    lat,
    lon,
    alt,
    trackInfo: alert.trackInfo
  };

  // Не подставляем null за отсутствующее поле — иначе merge затрёт REST postponeTo
  if ('readTime' in alert) {
    result.readTime = alert.readTime ?? null;
  }
  if ('postponeTo' in alert) {
    result.postponeTo = alert.postponeTo ?? null;
  }

  return result;
};

const convertOperatorMessageToAlert = (m: any): IAlert => {
  const defaultNotificationClassIcon = { color: '' };
  const { color } = notificationTypesData[m.class] || defaultNotificationClassIcon;

  const lat = typeof m.lat === 'number' ? m.lat : null;
  const lon = typeof m.lon === 'number' ? m.lon : null;
  const alt = typeof m.alt === 'number' ? m.alt : null;

  const conf = String(m?.templateData?.confirmationFlags || '').toUpperCase();
  const isNeedConfirmation = conf !== 'NONE' && conf !== '';
  const isUnread = !m.readTime;

  const result: IAlert = {
    uid: m.messageId || m.eventId,
    userId: m.userId,
    type: m.class,
    title: m.classFriendly,
    description: m.text || m.neutralText || '',
    color,
    isConfirm: isNeedConfirmation && isUnread,
    date: m.messageTime || m.sentTime || m.eventTime,
    severity: m.messageSeverity as NotificationSeverity,
    areaId: m.areaId ?? null,
    mapId: m.mapId ?? null,
    lat,
    lon,
    alt
  };

  if ('readTime' in m) {
    result.readTime = m.readTime ?? null;
  }
  if ('postponeTo' in m) {
    result.postponeTo = m.postponeTo ?? null;
  }

  return result;
};

export {
  isPostponeAvailable,
  mergeAlertUpdate,
  convertNotificationToAlert,
  convertWSAlertToAlert,
  convertOperatorMessageToAlert
};
